import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  Grid, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  Divider, 
  LinearProgress, 
  IconButton, 
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/system';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { ChevronLeft, ChevronRight, DragIndicator, Reorder } from "@mui/icons-material";

// --- Styled Components ---
const HeaderTitle = styled(Typography)(({ color, font, fontSize }) => ({
  fontFamily: font || 'serif',
  fontWeight: 'bold',
  letterSpacing: '2px',
  color: color || '#4e342e',
  textTransform: 'uppercase',
  fontSize: fontSize || '2.5rem',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
}));

const SectionHeader = styled('h3')(({ color, font, headingSize }) => ({
  fontFamily: font || 'sans-serif',
  fontWeight: 'bold',
  color: color || '#5d4037',
  textTransform: 'uppercase',
  marginBottom: '10px',
  paddingBottom: '5px',
  borderBottom: `2px solid ${color || '#795548'}`,
  fontSize: headingSize || '1.2rem',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  margin: '0 0 10px 0',
  padding: '0 0 5px 0',
}));

const InfoItem = styled(Box)(({ color, font, fontSize, fontStyle, lineSpacing, paragraphSpacing }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: paragraphSpacing !== undefined ? `${paragraphSpacing}px` : '8px',
  fontFamily: font || 'Arial, sans-serif',
  fontSize: fontSize || '14px',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineSpacing ? `${lineSpacing}px` : 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
    marginRight: '8px',
    marginTop: '2px',
    flexShrink: 0,
    color: color || '#795548',
  },
}));

const SkillBarContainer = styled(Box)(({ paragraphSpacing }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: paragraphSpacing !== undefined ? `${paragraphSpacing}px` : '8px',
  minWidth: 0,
}));

const SkillBarText = styled(Typography)(({ font, fontSize, fontStyle, lineSpacing }) => ({
  width: '100px',
  flexShrink: 0,
  fontFamily: font || 'Arial, sans-serif',
  fontSize: fontSize || '0.9rem',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineSpacing ? `${lineSpacing}px` : 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const BulletPoint = styled('span')(({ color }) => ({
  marginRight: '6px',
  flexShrink: 0,
  color: color || '#5d4037',
}));

const pageSizeMap = {
  A4:        { width: '794px',  minHeight: '1123px', heightPx: 1123 },
  Letter:    { width: '816px',  minHeight: '1056px', heightPx: 1056 },
  Legal:     { width: '816px',  minHeight: '1344px', heightPx: 1344 },
  A3:        { width: '1123px', minHeight: '1587px', heightPx: 1587 },
  Executive: { width: '696px',  minHeight: '1008px', heightPx: 1008 },
};

const defaultData = {
  firstName: "DONNA",
  lastName: "STROUPE",
  currentPosition: "Sales Representative",
  phone: "+1 456-789-0123",
  email: "hello@email.com",
  city: "Anywhere St, Anytown",
  website: "www.example.com",
  profile: "I am a Sales Representative with extensive experience in the fast-moving consumer goods industry. I am a professional who initiates and manages relationships with clients, who serves as their point of contact, and I am adept at following up with leads.",
};

const defaultLeftSectionOrder  = ['contact', 'skills', 'softwareSkills', 'languages', 'websites', 'certifications'];
const defaultRightSectionOrder = ['workExperience', 'volunteering', 'education', 'accomplishments', 'additionalInfo', 'references', 'interests'];

const safeText = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 0,
};

const Resume19 = ({
  color = '#795548',
  nameColor = '#4e342e',
  sidebarBackground = '#f5f5f5',
  headerBackground = '#fff',
  font = 'Arial, sans-serif',
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '24px',
  sectionSpacing = 30,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 30,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = 'A4',
  theme,
  formData = {},
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const primaryColor = theme?.primary || color;
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const palette = {
    mainBackground: '#FFFFFF',
    sidebarBackground,
    accentColor: primaryColor,
    textColor: '#333333',
    lightText: '#666666',
    header: headerBackground,
    nameColor,
  };

  const getFontSize = (multiplier = 1) => {
    const baseSize = parseInt(fontSize) || 14;
    return `${baseSize * multiplier}px`;
  };

  // ─── State ────────────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage]               = useState(0);
  const [pages, setPages]                           = useState([]);
  const mainContentRef    = useRef(null);
  const sidebarContentRef = useRef(null);

  const [educationEntries, setEducationEntries]         = useState([]);
  const [savedSkills, setSavedSkills]                   = useState([]);
  const [savedSummary, setSavedSummary]                 = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo]   = useState([]);
  const [savedLanguages, setSavedLanguages]             = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications]   = useState([]);
  const [savedReferences, setSavedReferences]           = useState([]);
  const [softwareSkills, setSoftwareSkills]             = useState([]);
  const [savedVolunteering, setSavedVolunteering]       = useState([]);
  const [savedInterests, setSavedInterests]             = useState([]);
  const [savedWebsites, setSavedWebsites]               = useState([]);
  const [customSections, setCustomSections]             = useState({});

  const [leftSectionOrder,  setLeftSectionOrder]  = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide,  setDragOverSide]  = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ─── Data Loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const se = localStorage.getItem("educationEntries"); if (se) setEducationEntries(JSON.parse(se));
      const ss = localStorage.getItem("skills");           if (ss) setSavedSkills(JSON.parse(ss));

      const sums = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (sums.length > 0) setSavedSummary(sums[sums.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")    || "[]"));
      setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")           || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")|| "[]"));
      setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList") || "[]"));
      setSavedReferences(JSON.parse(localStorage.getItem("referencesList")         || "[]"));
      setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")          || "[]"));
      setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")         || "[]"));
      setSavedInterests(JSON.parse(localStorage.getItem("interests")               || "[]"));
      setSavedWebsites(JSON.parse(localStorage.getItem("websites")                 || "[]"));

      // Custom sections
      const userId = localStorage.getItem("userId");
      let sectionsData = {};
      if (userId) {
        const current = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`) || "[]");
        current.forEach(name => {
          try {
            const raw = localStorage.getItem(`custom_${userId}_${name}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              const valid = (parsed.items || []).filter(i => i && i.trim() !== '');
              if (valid.length > 0) sectionsData[name] = valid;
            }
          } catch (e) {}
        });
      }
      setCustomSections(sectionsData);

      // Section orders
      const sl = localStorage.getItem("resume19LeftSectionOrder");
      if (sl) {
        try { setLeftSectionOrder(JSON.parse(sl).filter(s => !s.startsWith('custom_'))); }
        catch { setLeftSectionOrder(defaultLeftSectionOrder); }
      }

      const sr = localStorage.getItem("resume19RightSectionOrder");
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      if (sr) {
        try {
          const parsed = JSON.parse(sr).filter(s => !s.startsWith('custom_'));
          setRightSectionOrder([...parsed, ...customIds]);
        } catch { setRightSectionOrder([...defaultRightSectionOrder, ...customIds]); }
      } else {
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0)  localStorage.setItem("resume19LeftSectionOrder",  JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);
  useEffect(() => {
    if (rightSectionOrder.length > 0) localStorage.setItem("resume19RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  const formatDate = (month, year) => (!month || !year) ? "" : `${month} ${year}`;

  // ─── Drag & Drop ──────────────────────────────────────────────────────────────
  // FIX: Single unified drag handler — tracks source index + side + sectionId
  const handleDragStart = (e, index, side) => {
    const sectionId = side === 'left' ? leftSectionOrder[index] : rightSectionOrder[index];
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side, sectionId }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  // FIX: Unified drop — properly moves section between sides without losing content
  const handleDrop = (e, targetIndex, targetSide) => {
    e.preventDefault();
    e.currentTarget.style.opacity = '1';

    let parsed;
    try { parsed = JSON.parse(e.dataTransfer.getData('text/plain')); }
    catch { setDragOverIndex(null); setDragOverSide(null); return; }

    const { index: sourceIndex, side: sourceSide, sectionId } = parsed;

    setDragOverIndex(null);
    setDragOverSide(null);

    // Same side reorder
    if (sourceSide === targetSide) {
      if (sourceIndex === targetIndex) return;
      if (sourceSide === 'left') {
        const arr = [...leftSectionOrder];
        const [moved] = arr.splice(sourceIndex, 1);
        arr.splice(targetIndex, 0, moved);
        setLeftSectionOrder(arr);
      } else {
        const arr = [...rightSectionOrder];
        const [moved] = arr.splice(sourceIndex, 1);
        arr.splice(targetIndex, 0, moved);
        setRightSectionOrder(arr);
      }
      return;
    }

    // Cross-side move: remove from source, insert into target
    // FIX: use functional updates so both state changes use the latest arrays
    if (sourceSide === 'left' && targetSide === 'right') {
      setLeftSectionOrder(prev => {
        const arr = [...prev];
        arr.splice(sourceIndex, 1);
        return arr;
      });
      setRightSectionOrder(prev => {
        const arr = [...prev];
        arr.splice(targetIndex, 0, sectionId);
        return arr;
      });
    } else if (sourceSide === 'right' && targetSide === 'left') {
      setRightSectionOrder(prev => {
        const arr = [...prev];
        arr.splice(sourceIndex, 1);
        return arr;
      });
      setLeftSectionOrder(prev => {
        const arr = [...prev];
        arr.splice(targetIndex, 0, sectionId);
        return arr;
      });
    }
  };

  const resetLeftSectionOrder  = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => { const ids = Object.keys(customSections).map(n=>`custom_${n}`); setRightSectionOrder([...defaultRightSectionOrder,...ids]); };
  const resetAllSectionOrder   = () => { resetLeftSectionOrder(); resetRightSectionOrder(); };

  const getSectionTitle = (id) => {
    const titles = { contact:'Contact', skills:'Skills', softwareSkills:'Software Skills', languages:'Languages', interests:'Interests', certifications:'Certifications', workExperience:'Work Experience', volunteering:'Volunteering', education:'Education', accomplishments:'Accomplishments', additionalInfo:'Additional Information', references:'References', websites:'Websites/Profiles' };
    if (id.startsWith('custom_')) return id.replace('custom_','').charAt(0).toUpperCase() + id.replace('custom_','').slice(1);
    return titles[id] || id;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>({sections.length} sections)</Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => handleDragStart(e, index, side)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index, side)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' },
              transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        )) : (
          // FIX: Drop zone even when empty — allows dropping into empty side
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOverIndex(0); setDragOverSide(side); }}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 0, side)}
            sx={{
              p: 3, textAlign: 'center', border: '2px dashed #e0e0e0', borderRadius: 1,
              backgroundColor: dragOverSide === side ? '#f5f5f5' : 'transparent',
              transition: 'background-color 0.2s ease',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              No sections. Drag sections here from the other side.
            </Typography>
          </Box>
        )}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  // ─── Section Content Renderer (shared by both sides) ──────────────────────────
  // FIX: Single renderSection function — renders any sectionId regardless of which side it's on
  const renderSection = (sectionId, containerClass) => {
    const isLeft = containerClass === 'sidebar-section';

    switch (sectionId) {
      case 'contact':
        return (
          <div key="contact" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                    <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Contact</SectionHeader>
              {data.phone && (
                <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing} paragraphSpacing={paragraphSpacing}>
                  <PhoneIcon />
                  <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, color:'black', ...safeText }}>{data.phone}</Typography>
                </InfoItem>
              )}
              {data.email && (
                <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing} paragraphSpacing={paragraphSpacing}>
                  <EmailIcon />
                  <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, color:'black', ...safeText }}>{data.email}</Typography>
                </InfoItem>
              )}
              {data.city && (
                <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing} paragraphSpacing={paragraphSpacing}>
                  <HomeIcon />
                  <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, color:'black', ...safeText }}>{data.city}</Typography>
                </InfoItem>
              )}
              {data.linkedIn && (
                <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing} paragraphSpacing={paragraphSpacing}>
                  <LinkedInIcon />
                  <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, color:'black', wordBreak:'break-all' }}>{data.linkedIn}</Typography>
                </InfoItem>
              )}
            </Box>
          </div>
        );

      case 'skills':
        return savedSkills.length > 0 ? (
          <div key="skills" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Skills</SectionHeader>
              {savedSkills.map((skill, i) => (
                <SkillBarContainer key={i} paragraphSpacing={paragraphSpacing}>
                  <SkillBarText font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>{skill.name}</SkillBarText>
                  <LinearProgress variant="determinate" value={skill.rating||0} sx={{ flexGrow:1, height:8, borderRadius:5, backgroundColor:'#e0e0e0', minWidth:0, '& .MuiLinearProgress-bar':{ backgroundColor:palette.accentColor } }} />
                </SkillBarContainer>
              ))}
            </Box>
          </div>
        ) : null;

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <div key="softwareSkills" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Software Skills</SectionHeader>
              {softwareSkills.map((skill, i) => (
                <SkillBarContainer key={i} paragraphSpacing={paragraphSpacing}>
                  <SkillBarText font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>{skill.name}</SkillBarText>
                  <LinearProgress variant="determinate" value={skill.level||skill.rating||0} sx={{ flexGrow:1, height:8, borderRadius:5, backgroundColor:'#e0e0e0', minWidth:0, '& .MuiLinearProgress-bar':{ backgroundColor:palette.accentColor } }} />
                </SkillBarContainer>
              ))}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Languages</SectionHeader>
              {savedLanguages.map((lang, i) => (
                <SkillBarContainer key={i} paragraphSpacing={paragraphSpacing}>
                  <SkillBarText font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>{lang.name}</SkillBarText>
                  <LinearProgress variant="determinate" value={lang.level ? parseInt(lang.level) : 100} sx={{ flexGrow:1, height:8, borderRadius:5, backgroundColor:'#e0e0e0', minWidth:0, '& .MuiLinearProgress-bar':{ backgroundColor:palette.accentColor } }} />
                </SkillBarContainer>
              ))}
            </Box>
          </div>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <div key="websites" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Websites / Profiles</SectionHeader>
              <List dense disablePadding>
                {savedWebsites.map((site, i) => (
                  <ListItem key={i} sx={{ py:0.5, px:0, display:'flex', alignItems:'flex-start', mb: paragraphSpacing / 20 }}>
                    <BulletPoint color={palette.accentColor}>•</BulletPoint>
                    <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, wordBreak:'break-all' }}>
                      <a href={site.url.startsWith('http') ? site.url : `https://${site.url}`} style={{ color:palette.accentColor, textDecoration:'none', fontFamily:font }} target="_blank" rel="noopener noreferrer">
                        {site.name||site.url}
                      </a>
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Certifications</SectionHeader>
              <List dense disablePadding>
                {savedCertifications.map((cert, i) => (
                  <ListItem key={i} sx={{ py:0.5, px:0, display:'flex', alignItems:'flex-start', mb: paragraphSpacing / 20 }}>
                    <BulletPoint color={palette.accentColor}>•</BulletPoint>
                    <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{cert.name} - {cert.provider} ({cert.year})</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Work Experience</SectionHeader>
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: paragraphSpacing / 10 }}>
                  <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight:'bold', color:palette.textColor, fontFamily:font, fontSize:getFontSize(1.1), fontStyle, lineHeight:`${lineSpacing}px`, flex:'1 1 auto', ...safeText }}>{work.jobTitle}</Typography>
                    <Typography variant="body2" sx={{ fontStyle:'italic', color:palette.lightText, fontFamily:font, fontSize, lineHeight:`${lineSpacing}px`, flexShrink:0 }}>
                      {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ color:palette.accentColor, mb:1, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{work.employer}</Typography>
                  {work.description && (
                    <Box
                      sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, textIndent:`${paragraphIndent}px`, ...safeText,
                        "& strong":{ fontWeight:"bold" }, "& em":{ fontStyle:"italic" }, "& u":{ textDecoration:"underline" } }}
                      dangerouslySetInnerHTML={{ __html: work.description
                        .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font};font-weight:bold;font-size:${fontSize};line-height:${lineSpacing}px">$1</strong>`)
                        .replace(/\*(.*?)\*/g, `<em style="font-family:${font};font-style:italic;font-size:${fontSize};line-height:${lineSpacing}px">$1</em>`)
                        .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font};font-size:${fontSize};line-height:${lineSpacing}px">$1</u>`)
                        .replace(/\n/g, `<br style="font-family:${font}" />`)
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Volunteering Experience</SectionHeader>
              {savedVolunteering.map((vol, i) => (
                <Box key={i} sx={{ mb: paragraphSpacing / 10 }}>
                  <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight:'bold', color:palette.textColor, fontFamily:font, fontSize:getFontSize(1.1), fontStyle, lineHeight:`${lineSpacing}px`, flex:'1 1 auto', ...safeText }}>{vol.subtopic || vol.role}</Typography>
                    <Typography variant="body2" sx={{ fontStyle:'italic', color:palette.lightText, fontFamily:font, fontSize, lineHeight:`${lineSpacing}px`, flexShrink:0 }}>
                      {vol.fromDate} - {vol.toDate}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ color:palette.accentColor, mb:1, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{vol.organization}</Typography>
                  {vol.content && (
                    <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, textIndent:`${paragraphIndent}px`, ...safeText }}>{vol.content}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Education</SectionHeader>
              {educationEntries.map((edu, i) => {
                const degree = edu.educationLevel === 'school' ? `${edu.standard||''} Standard` : `${edu.degreeName||''}`;
                const fieldOfStudy = edu.educationLevel === 'school' ? edu.stream||'' : edu.specialization||edu.degreeType||'';
                const schoolName = edu.educationLevel === 'school' ? edu.schoolName||'' : edu.collegeName||'';
                const schoolLocation = edu.educationLevel === 'school' ? edu.schoolLocation||'' : edu.collegeLocation||'';
                let details = [];
                if (edu.educationLevel === 'school') {
                  if (edu.district) details.push(`District: ${edu.district}`);
                  if (edu.percentage) details.push(`Percentage: ${edu.percentage}%`);
                } else {
                  if (edu.programType) details.push(`Program: ${edu.programType}`);
                  if (edu.cgpa) details.push(`CGPA: ${edu.cgpa}`);
                  if (edu.grade) details.push(`Grade: ${edu.grade}`);
                  if (edu.percentage) details.push(`Percentage: ${edu.percentage}%`);
                }
                const additionalDetails = edu.additionalCoursework
                  ? (details.length > 0 ? `${details.join(', ')} | ${edu.additionalCoursework}` : edu.additionalCoursework)
                  : details.join(', ');
                return (
                  <Box key={i} sx={{ mb: paragraphSpacing / 10 }}>
                    <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:0.5 }}>
                      <Typography variant="h6" sx={{ fontWeight:'bold', color:palette.textColor, fontFamily:font, fontSize:getFontSize(1.1), fontStyle, lineHeight:`${lineSpacing}px`, flex:'1 1 auto', ...safeText }}>
                        {degree}{fieldOfStudy && ` - ${fieldOfStudy}`}
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle:'italic', color:palette.lightText, fontFamily:font, fontSize, lineHeight:`${lineSpacing}px`, flexShrink:0 }}>
                        {edu.gradMonth && edu.gradYear ? formatDate(edu.gradMonth, edu.gradYear) : (edu.educationLevel === 'school' ? 'Completed' : '')}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle1" sx={{ color:palette.accentColor, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>
                      {schoolName}{schoolLocation && `, ${schoolLocation}`}
                    </Typography>
                    {additionalDetails && (
                      <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, textIndent:`${paragraphIndent}px`, mt:0.5, ...safeText }}>{additionalDetails}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Accomplishments</SectionHeader>
              <List disablePadding>
                {savedAccomplishments.map((acc, i) => (
                  <ListItem key={i} sx={{ py:0.5, px:0, display:'flex', alignItems:'flex-start', mb: paragraphSpacing / 20 }}>
                    <BulletPoint color={palette.accentColor}>•</BulletPoint>
                    <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{acc}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Additional Info</SectionHeader>
              <List dense disablePadding>
                {savedAdditionalInfo.map((info, i) => (
                  <ListItem key={i} sx={{ py:0.5, px:0, display:'flex', alignItems:'flex-start', mb: paragraphSpacing / 20 }}>
                    <BulletPoint color={palette.accentColor}>•</BulletPoint>
                    <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{info}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>References</SectionHeader>
              <Box sx={{ display:'flex', flexWrap:'wrap', gap:2 }}>
                {savedReferences.map((ref, i) => (
                  <Box key={i} sx={{ minWidth:'45%', flex:'1 1 45%', mb: paragraphSpacing / 10 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily:font, fontStyle, fontSize, lineHeight:`${lineSpacing}px`, ...safeText }}>{ref.name||ref}</Typography>
                    {ref.position && <Typography variant="body2" sx={{ color:palette.lightText, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{ref.position}</Typography>}
                    {ref.company  && <Typography variant="body2" sx={{ color:palette.lightText, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{ref.company}</Typography>}
                    {ref.phone    && <Typography variant="body2" sx={{ color:palette.lightText, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, wordBreak:'break-all' }}>{ref.phone}</Typography>}
                    {ref.email    && <Typography variant="body2" sx={{ color:palette.lightText, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, wordBreak:'break-all' }}>{ref.email}</Typography>}
                  </Box>
                ))}
              </Box>
            </Box>
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className={containerClass}>
            <Box sx={{ mb: sectionSpacing/10 }}>
                            <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>Interests</SectionHeader>
              <List dense disablePadding>
                {savedInterests.map((interest, i) => (
                  <ListItem key={i} sx={{ py:0.5, px:0, display:'flex', alignItems:'flex-start', mb: paragraphSpacing / 20 }}>
                    <BulletPoint color={palette.accentColor}>•</BulletPoint>
                    <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{interest}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </div>
        ) : null;

      default:
        if (sectionId.startsWith('custom_')) {
          const name = sectionId.replace('custom_','');
          const items = customSections[name] || [];
          return items.length > 0 ? (
            <div key={sectionId} className={containerClass}>
              <Box sx={{ mb: sectionSpacing/10 }}>
                                <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize}>{name.charAt(0).toUpperCase()+name.slice(1)}</SectionHeader>
                {containerClass === 'sidebar-section' ? (
                  <List dense disablePadding>
                    {items.map((item, i) => (
                      <ListItem key={i} sx={{ py:0.5, px:0, display:'flex', alignItems:'flex-start', mb: paragraphSpacing / 20 }}>
                        <BulletPoint color={palette.accentColor}>•</BulletPoint>
                        <Typography variant="body2" sx={{ fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>{item}</Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  items.map((item, i) => (
                    <Typography key={i} sx={{ mb:`${paragraphSpacing/10}px`, fontSize, fontFamily:font, fontStyle, lineHeight:`${lineSpacing}px`, textIndent:`${paragraphIndent}px`, ...safeText }} whiteSpace="pre-line">
                      {item}
                    </Typography>
                  ))
                )}
              </Box>
            </div>
          ) : null;
        }
        return null;
    }
  };

  // FIX: Both sidebar and main content now use renderSection with appropriate class
  const renderSidebarSections  = () => leftSectionOrder.map(id => renderSection(id, 'sidebar-section')).filter(Boolean);
  const renderMainContentSections = () => rightSectionOrder.map(id => renderSection(id, 'resume-section')).filter(Boolean);

  // ─── Header ───────────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <Box sx={{ mb: sectionSpacing/10 }}>
      <HeaderTitle color={palette.nameColor} font={font} fontSize={headingSize} variant="h3">
        {data.firstName} {data.lastName}
      </HeaderTitle>
      <Typography variant="h6" sx={{ color:palette.lightText, mt:1, textTransform:'uppercase', fontFamily:font, fontSize:getFontSize(1.2), fontStyle, lineHeight:`${lineSpacing}px`, ...safeText }}>
        {data.currentPosition}
      </Typography>
      <SectionHeader color={palette.accentColor} font={font} headingSize={headingSize} sx={{ mt: 2 }}>
        Profile
      </SectionHeader>
      <Typography variant="body1" sx={{ mt:1, color:palette.textColor, fontFamily:font, fontSize, fontStyle, lineHeight:`${lineSpacing}px`, textIndent:`${paragraphIndent}px`, ...safeText }}>
        {savedSummary || data.profile}
      </Typography>
    </Box>
  );

  // ─── addFontStylesToHTML: inject inline styles into re-injected outerHTML ─────
  const addFontStylesToHTML = (html) => {
    if (!html) return '';
    return html
      // Section divs — apply sectionSpacing as margin-bottom
      .replace(/<div class="resume-section"/g,   `<div class="resume-section" style="margin-bottom:${sectionSpacing}px;"`)
      .replace(/<div class="sidebar-section"/g,  `<div class="sidebar-section" style="margin-bottom:${sectionSpacing}px;"`)
      // h3 headings (SectionHeader) — apply headingSize
      .replace(/<h3/g, `<h3 style="font-family:${font};font-size:${headingSize};font-weight:bold;color:${palette.accentColor};text-transform:uppercase;margin:0 0 10px 0;padding:0 0 5px 0;border-bottom:2px solid ${palette.accentColor};word-break:break-word;"`)
      // p tags — apply fontSize, lineSpacing, paragraphSpacing
      .replace(/<p/g, `<p style="font-family:${font};font-size:${fontSize};font-style:${fontStyle};line-height:${lineSpacing}px;margin-bottom:${paragraphSpacing}px;"`)
      // span tags
      .replace(/<span/g, `<span style="font-family:${font};font-style:${fontStyle};"`)
      // li items
      .replace(/<li/g, `<li style="font-family:${font};font-size:${fontSize};font-style:${fontStyle};line-height:${lineSpacing}px;margin-bottom:${paragraphSpacing/2}px;"`)
      .replace(/<ul/g, `<ul style="font-family:${font};margin-bottom:${paragraphSpacing}px;padding-left:16px;"`)
      .replace(/<ol/g, `<ol style="font-family:${font};margin-bottom:${paragraphSpacing}px;padding-left:16px;"`);
  };

  // ─── Pagination ───────────────────────────────────────────────────────────────
  const paginateContent = useCallback(() => {
    if (!mainContentRef.current || !sidebarContentRef.current) return;

    const mainSections    = Array.from(mainContentRef.current.querySelectorAll('.resume-section'));
    const sidebarSections = Array.from(sidebarContentRef.current.querySelectorAll('.sidebar-section'));

    const pageHeightInPx  = page.heightPx;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - 60; // 60px header buffer

    let newPages = [];
    let mainIdx = 0, sidebarIdx = 0;

    while (mainIdx < mainSections.length || sidebarIdx < sidebarSections.length) {
      let pageMains = [], pageSidebars = [];
      let currentMainHeight = 0, currentSidebarHeight = 0;

      // Fill sidebar for this page
      while (sidebarIdx < sidebarSections.length) {
        const h = sidebarSections[sidebarIdx].offsetHeight + sectionSpacing;
        if (currentSidebarHeight + h <= availableHeight) {
          pageSidebars.push(addFontStylesToHTML(sidebarSections[sidebarIdx].outerHTML));
          currentSidebarHeight += h;
          sidebarIdx++;
        } else break;
      }
      // Fill main content for this page
      while (mainIdx < mainSections.length) {
        const h = mainSections[mainIdx].offsetHeight + sectionSpacing;
        if (currentMainHeight + h <= availableHeight) {
          pageMains.push(addFontStylesToHTML(mainSections[mainIdx].outerHTML));
          currentMainHeight += h;
          mainIdx++;
        } else break;
      }

      if (pageMains.length > 0 || pageSidebars.length > 0) {
        newPages.push({ main: [...pageMains], sidebar: [...pageSidebars] });
      } else {
        // Safety: advance one section to avoid infinite loop
        if (sidebarIdx < sidebarSections.length) {
          newPages.push({ main: [], sidebar: [addFontStylesToHTML(sidebarSections[sidebarIdx].outerHTML)] });
          sidebarIdx++;
        } else if (mainIdx < mainSections.length) {
          newPages.push({ main: [addFontStylesToHTML(mainSections[mainIdx].outerHTML)], sidebar: [] });
          mainIdx++;
        } else break;
      }
    }

    if (newPages.length === 0) newPages.push({ main: [], sidebar: [] });
    setPages(newPages);
  }, [page.heightPx, topBottomMargin, sectionSpacing, leftSectionOrder, rightSectionOrder, font, fontSize, fontStyle, lineSpacing, paragraphIndent, headingSize, paragraphSpacing]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 150);
    return () => clearTimeout(id);
  }, [
    paginateContent,
    educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo,
    savedLanguages, savedAccomplishments, savedCertifications, savedReferences,
    softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections,
    leftSectionOrder, rightSectionOrder, color, font, fontSize, fontStyle, lineSpacing,
    paragraphIndent, sectionSpacing,
  ]);

  // ─── Hidden measurement boxes ─────────────────────────────────────────────────
  const pageWidthNum = parseInt(page.width.replace('px',''), 10);

  const renderMainContentToMeasure = () => (
    <Box ref={mainContentRef} sx={{
      visibility:'hidden', position:'absolute', left:'-9999px', top:0,
      width: `${pageWidthNum * 0.70}px`,
      p: `${topBottomMargin}px ${Math.max(sideMargins/5, 8)}px`,
      boxSizing:'border-box', fontFamily:font,
    }}>
      {renderMainContentSections()}
    </Box>
  );

  const renderSidebarContentToMeasure = () => (
    <Box ref={sidebarContentRef} sx={{
      visibility:'hidden', position:'absolute', left:'-9999px', top:0,
      width: `${pageWidthNum * 0.30}px`,
      p: `${topBottomMargin}px ${Math.max(sideMargins/5, 8)}px`,
      boxSizing:'border-box', fontFamily:font,
    }}>
      {renderSidebarSections()}
    </Box>
  );

  const pageBoxStyles = {
    width: page.width,
    height: page.minHeight,
    minHeight: page.minHeight,
    mx: 'auto',
    bgcolor: palette.header,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    boxSizing: 'border-box',
    border: `${lineWeight}px solid ${palette.accentColor}`,
    pageBreakAfter: 'always',
    '@media print': {
      width: page.width,
      height: page.minHeight,
      minHeight: page.minHeight,
      boxShadow: 'none',
      m: 0,
      overflow: 'hidden',
      border: 'none',
    },
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width:'100%', position:'relative', m:5, fontFamily:font }}>
      {!exporting && (
        <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', mb:2, gap:2, '@media print':{ display:'none' } }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p-1))} disabled={currentPage===0} color="primary"><ChevronLeft /></IconButton>
          <Typography sx={{ mx:2 }}>Page {currentPage+1} of {Math.max(1, pages.length)}</Typography>
          <IconButton onClick={() => setCurrentPage(p => Math.min(pages.length-1, p+1))} disabled={currentPage===Math.max(0,pages.length-1)} color="primary"><ChevronRight /></IconButton>
          {enableDragDrop && (
            <Box sx={{ display:'flex', gap:2, alignItems:'center', ml:2 }}>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary">Arrange Sections</Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">Reset All Sections</Button>
            </Box>
          )}
        </Box>
      )}

      {renderMainContentToMeasure()}
      {renderSidebarContentToMeasure()}

      <Box id="resume-container">
        {pages.length > 0 ? pages.map((pageContent, index) => (
          <Box key={index} className="resume-page" sx={{
            ...pageBoxStyles,
            boxShadow: !exporting && index===currentPage ? 4 : 0,
            display: exporting ? 'flex' : (index===currentPage ? 'flex' : 'none'),
            '@media print': { display:'flex' }
          }}>
            {/* Sidebar — 30% */}
            <Box sx={{
              width: '30%',
              flexShrink: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
              minWidth: 0,
              bgcolor: palette.sidebarBackground,
              p: `${topBottomMargin}px ${Math.max(sideMargins/5, 8)}px`,
              display: 'flex',
              flexDirection: 'column',
              fontFamily: font,
            }}>
              {index === 0 && <Box sx={{ mb: sectionSpacing/10, display:{ xs:'block', md:'none' } }}>{renderHeader()}</Box>}
              {pageContent.sidebar.map((s, i) => (
                <div key={i} style={{ wordBreak:'break-word', overflowWrap:'break-word' }} dangerouslySetInnerHTML={{ __html: s }} />
              ))}
              <Box sx={{ flexGrow:1 }} />
            </Box>

            {/* Main content — 70% */}
            <Box sx={{
              width: '70%',
              flexShrink: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
              minWidth: 0,
              p: `${topBottomMargin}px ${Math.max(sideMargins/5, 8)}px`,
              fontFamily: font,
            }}>
              {index === 0 && <Box sx={{ mb: sectionSpacing/10, display:{ xs:'none', md:'block' } }}>{renderHeader()}</Box>}
              {pageContent.main.map((s, i) => (
                <div key={i} style={{ wordBreak:'break-word', overflowWrap:'break-word' }} dangerouslySetInnerHTML={{ __html: s }} />
              ))}
            </Box>
          </Box>
        )) : (
          // Empty fallback
          <Box className="resume-page" sx={{ ...pageBoxStyles, boxShadow:4, display:'flex' }}>
            <Box sx={{ width:'30%', flexShrink:0, bgcolor:palette.sidebarBackground, p:`${topBottomMargin}px ${Math.max(sideMargins/5,8)}px`, boxSizing:'border-box', overflow:'hidden', display:'flex', flexDirection:'column' }}>
              <Box sx={{ mb: sectionSpacing/10, display:{ xs:'block', md:'none' } }}>{renderHeader()}</Box>
              {renderSidebarSections()}
              <Box sx={{ flexGrow:1 }} />
            </Box>
            <Box sx={{ width:'70%', flexShrink:0, p:`${topBottomMargin}px ${Math.max(sideMargins/5,8)}px`, boxSizing:'border-box', overflow:'hidden' }}>
              <Box sx={{ mb: sectionSpacing/10, display:{ xs:'none', md:'block' } }}>{renderHeader()}</Box>
              {renderMainContentSections()}
            </Box>
          </Box>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center"><Reorder sx={{ mr:1 }} />Arrange Resume Sections</Box></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb:3 }}>
            Drag and drop sections to reorder them within each side or move sections between left sidebar and right content.
          </Typography>
          <Box sx={{ display:"flex", flexDirection:{ xs:"column", md:"row" }, gap:3, width:"100%" }}>
            <Box sx={{ flex:1, width:"50%" }}>{renderDragPanel("left",  leftSectionOrder)}</Box>
            <Box sx={{ flex:1, width:"50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#795548', label: 'Brown'  },
    { value: '#3f51b5', label: 'Indigo' },
    { value: '#2196f3', label: 'Blue'   },
    { value: '#f44336', label: 'Red'    },
    { value: '#4caf50', label: 'Green'  },
    { value: '#ff9800', label: 'Orange' },
    { value: '#9c27b0', label: 'Purple' }
  ],
  nameColorOptions: [
    { value: '#4e342e', label: 'Dark Brown' },
    { value: '#333333', label: 'Dark Gray'  },
    { value: '#000000', label: 'Black'      },
    { value: '#1a2b47', label: 'Dark Blue'  },
    { value: '#2e7d32', label: 'Green'      }
  ],
  sidebarBackgroundOptions: [
    { value: '#f5f5f5',   label: 'Light Gray'     },
    { value: '#e6f3f9ff', label: 'Light Blue'     },
    { value: '#f0f8e6ff', label: 'Light Green'    },
    { value: '#f9e6f3ff', label: 'Light Pink'     },
    { value: '#e6e6f9ff', label: 'Light Lavender' },
    { value: '#f9f3e6ff', label: 'Light Beige'    }
  ],
  headerBackgroundOptions: [
    { value: '#fff',     label: 'White'            },
    { value: '#f8f9fa',  label: 'Light Gray'       },
    { value: '#e3f2fd',  label: 'Very Light Blue'  },
    { value: '#f3e5f5',  label: 'Very Light Purple'},
    { value: '#e8f5e9',  label: 'Very Light Green' }
  ],
  experienceLevel: ["No Experience", "Less than 3 years", "3-5 Years"]
};

export default Resume19;